import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

interface COVIDDataTableProps {
  covidData: any[];
  snfAdmitsCnt?: number;
}

export const COVIDDataTable: React.FC<COVIDDataTableProps> = ({ covidData, snfAdmitsCnt }) => {
  if (!covidData || covidData.length === 0) {
    return (
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="text.secondary">
          No COVID weekly report data available for the 21-day window around this date.
        </Typography>
      </Box>
    );
  }

  // Separate the COVID data by period
  const beforeData = covidData.filter((d: any) => d.period === 'before');
  const sameDayData = covidData.filter((d: any) => d.period === 'same_day');
  const afterData = covidData.filter((d: any) => d.period === 'after');

  const rows: React.ReactNode[] = [];

  // Add "before" rows
  beforeData.forEach((covidDay: any, idx: number) => {
    // Check if the row has any COVID data (any non-null values)
    const hasCovidData = (
      covidDay.residents_confirmed != null ||
      covidDay.staff_confirmed != null ||
      covidDay.resident_deaths != null ||
      covidDay.occupied_beds != null
    );

    rows.push(
      <TableRow key={`before-${idx}`} sx={{ bgcolor: hasCovidData ? '#fff3cd' : '#f8f9fa' }}>
        <TableCell sx={{ fontSize: '0.75rem', minWidth: 100, whiteSpace: 'nowrap' }}>
          <Typography variant="caption">{covidDay.date}</Typography>
        </TableCell>
        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: covidDay.snf_admits != null ? 'bold' : 'normal' }}>
          {covidDay.snf_admits ?? '-'}
        </TableCell>
        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: covidDay.residents_confirmed != null ? 'bold' : 'normal' }}>
          {covidDay.residents_confirmed ?? ''}
        </TableCell>
        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: covidDay.staff_confirmed != null ? 'bold' : 'normal' }}>
          {covidDay.staff_confirmed ?? ''}
        </TableCell>
        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: covidDay.resident_deaths != null ? 'bold' : 'normal' }}>
          {covidDay.resident_deaths ?? ''}
        </TableCell>
        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: covidDay.occupied_beds != null ? 'bold' : 'normal' }}>
          {covidDay.occupied_beds ?? ''}
        </TableCell>
      </TableRow>
    );
  });

  // Add "same day" row (scheme instance row in green)
  sameDayData.forEach((covidDay: any, idx: number) => {
    rows.push(
      <TableRow key={`same-${idx}`} sx={{ bgcolor: '#d4edda', fontWeight: 'bold' }}>
        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold', minWidth: 100, whiteSpace: 'nowrap' }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
            {covidDay.date}
          </Typography>
        </TableCell>
        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
          {snfAdmitsCnt || '-'}
        </TableCell>
        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: covidDay.residents_confirmed != null ? 'bold' : 'normal' }}>
          {covidDay.residents_confirmed ?? ''}
        </TableCell>
        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: covidDay.staff_confirmed != null ? 'bold' : 'normal' }}>
          {covidDay.staff_confirmed ?? ''}
        </TableCell>
        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: covidDay.resident_deaths != null ? 'bold' : 'normal' }}>
          {covidDay.resident_deaths ?? ''}
        </TableCell>
        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: covidDay.occupied_beds != null ? 'bold' : 'normal' }}>
          {covidDay.occupied_beds ?? ''}
        </TableCell>
      </TableRow>
    );
  });

  // Add "after" rows
  afterData.forEach((covidDay: any, idx: number) => {
    // Check if the row has any COVID data (any non-null values)
    const hasCovidData = (
      covidDay.residents_confirmed != null ||
      covidDay.staff_confirmed != null ||
      covidDay.resident_deaths != null ||
      covidDay.occupied_beds != null
    );

    rows.push(
      <TableRow key={`after-${idx}`} sx={{ bgcolor: hasCovidData ? '#fff3cd' : '#f8f9fa' }}>
        <TableCell sx={{ fontSize: '0.75rem', minWidth: 100, whiteSpace: 'nowrap' }}>
          <Typography variant="caption">{covidDay.date}</Typography>
        </TableCell>
        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: covidDay.snf_admits != null ? 'bold' : 'normal' }}>
          {covidDay.snf_admits ?? '-'}
        </TableCell>
        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: covidDay.residents_confirmed != null ? 'bold' : 'normal' }}>
          {covidDay.residents_confirmed ?? ''}
        </TableCell>
        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: covidDay.staff_confirmed != null ? 'bold' : 'normal' }}>
          {covidDay.staff_confirmed ?? ''}
        </TableCell>
        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: covidDay.resident_deaths != null ? 'bold' : 'normal' }}>
          {covidDay.resident_deaths ?? ''}
        </TableCell>
        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: covidDay.occupied_beds != null ? 'bold' : 'normal' }}>
          {covidDay.occupied_beds ?? ''}
        </TableCell>
      </TableRow>
    );
  });

  return (
    <Box sx={{ flex: 1, position: 'relative' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        <Typography component="span" sx={{ fontWeight: 600 }}>
          COVID Reporting Data
        </Typography>
        <Typography component="span" sx={{ fontWeight: 'normal' }}>
          {' '}(+/- 21 Days)
        </Typography>
      </Typography>
      <Box
        sx={{
          maxHeight: 500,
          overflowY: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  bgcolor: 'background.paper',
                  borderBottom: 2,
                  borderColor: 'divider',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  minWidth: 100,
                  whiteSpace: 'nowrap'
                }}
              >
                Date
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  bgcolor: 'background.paper',
                  borderBottom: 2,
                  borderColor: 'divider',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}
              >
                SNF Admits (daily)
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  bgcolor: 'background.paper',
                  borderBottom: 2,
                  borderColor: 'divider',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}
              >
                Residents Confirmed COVID-19 (weekly)
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  bgcolor: 'background.paper',
                  borderBottom: 2,
                  borderColor: 'divider',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}
              >
                Staff Confirmed COVID-19 (weekly)
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  bgcolor: 'background.paper',
                  borderBottom: 2,
                  borderColor: 'divider',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}
              >
                Residents COVID-19 Deaths (weekly)
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  bgcolor: 'background.paper',
                  borderBottom: 2,
                  borderColor: 'divider',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}
              >
                Occupied Beds (weekly)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{rows}</TableBody>
        </Table>
      </Box>
    </Box>
  );
};